// @ts-nocheck

import { useState, useEffect } from 'react';

import { OutsideClickHandler } from '../../Search';
import FrequentlyBoughtTogether from '../../FrequentlyBoughtTogether';

import OfferIcon from '../../../assets/offer.svg?react';
import { safeTrack } from '../../../utils/analytics';

interface RecommendationsModalProps {
  customer: string;
  store: string;
  provider: string;
  open: boolean;
  currency?: string;
  locale?: 'ar-SA' | 'en-US';
  primaryColor?: string;
  product?: string;
  preloadedProducts?: any;
  language?: string;
  fptTitle?: string;
  fptTitleEn?: string;
}

const RecommendationsModal: React.FC<RecommendationsModalProps> = ({
  customer,
  store,
  provider,
  open,
  currency = 'SAR',
  locale = 'ar-SA',
  primaryColor = '#000000',
  product,
  preloadedProducts,
  language = 'ar',
  fptTitle,
  fptTitleEn,
}) => {
  const [openDialog, setOpenDialog] = useState(open);
  const [loading, setLoading] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = () => {
    setIsClosing(true);

    // Set timestamp to prevent modal from reopening immediately
    localStorage.setItem(
      'alfinder_fbt_modal_last_close',
      Date.now().toString(),
    );

    // Save "don't show again" preference if checked
    if (dontShowAgain) {
      localStorage.setItem('alfinder_fbt_modal_dont_show_again', 'true');
    }

    // Wait for animation to complete before closing
    setTimeout(() => {
      setOpenDialog(false);
    }, 300);
  };

  // Remove the modal container from DOM when modal closes
  useEffect(() => {
    if (!openDialog) {
      // Small delay to allow any closing animations to complete
      const timer = setTimeout(() => {
        const modalContainer = document.getElementById('recommendationsModal');
        if (modalContainer) {
          modalContainer.remove();
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [openDialog]);

  useEffect(() => {
    safeTrack('fbt_modal_viewed', {
      store,
      provider,
      customer,
      product,
    });
  }, []);

  if (!openDialog) return null;

  return (
    <div
      className={`recommendations-modal-backdrop ${isClosing ? 'closing' : ''}`}
    >
      <OutsideClickHandler onOutsideClick={handleClose}>
        <div
          className={`recommendations-modal-content ${
            isClosing ? 'closing' : ''
          }`}
          style={
            {
              '--modal-primary-color': primaryColor,
            } as React.CSSProperties
          }
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              padding: '5px',
            }}
          >
            <button
              className="recommendations-modal-close"
              onClick={handleClose}
              aria-label="Close modal"
            >
              ×
            </button>
          </div>
          <div className="recommendations-modal-header">
            <div
              style={{
                border: `1px solid ${primaryColor}`,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '90px',
                height: '90px',
                marginBottom: '10px',
              }}
            >
              <OfferIcon
                style={{ width: '50px', height: '50px', color: primaryColor }}
              />
            </div>
            <h2> {language === 'ar' ? fptTitle : fptTitleEn}</h2>
          </div>

          <div className="recommendations-modal-body">
            <FrequentlyBoughtTogether
              currency={currency}
              locale={locale}
              primaryColor={primaryColor}
              store={store}
              provider={provider}
              product={product}
              customer={customer}
              hideTitle={true}
              skipInitialLoading={true}
              preloadedProducts={preloadedProducts}
              onModalClose={handleClose}
              type="modal"
              language={language}
              fptTitle={fptTitle}
              fptTitleEn={fptTitleEn}
            />
          </div>

          <div className="recommendations-modal-footer">
            <div className="recommendations-modal-checkbox-container">
              <div className="modal-checkbox-wrapper">
                <input
                  type="checkbox"
                  id="dont-show-again-checkbox"
                  checked={dontShowAgain}
                  onChange={(e) => {
                    setDontShowAgain(e.target.checked);
                    safeTrack('fbt_modal_dont_show_again', {
                      store,
                      provider,
                      customer,
                      product,
                    });
                  }}
                  className="modal-checkbox-input"
                />
                <label
                  htmlFor="dont-show-again-checkbox"
                  className="modal-checkbox-label"
                ></label>
              </div>
              <span>
                {language === 'ar' ? 'لا تذكرني مرة اخرى' : "Don't show again"}
              </span>
            </div>
          </div>
        </div>
      </OutsideClickHandler>
    </div>
  );
};

export default RecommendationsModal;
